import { ButtonContent } from '../ui/ButtonContent';
import { useEffect, useRef, useState } from 'react';
import { IconDownload, IconFileSpreadsheet } from '@tabler/icons-react';
import type { InterviewSession } from '../../types';
import { readPreparedExport, validateExportFilters } from '../../lib/campaign-export-filters';
import { downloadCampaignCsv } from '../../services/campaignExportService';
import { captureException } from '../../lib/telemetry/posthog/frontend';
import { CampaignExportModal } from './CampaignExportModal';

/** A durable download action whose preferences are validated again against the current campaign. */
export function CampaignCsvExportCard({ value, campaignId }: { value: unknown; campaignId: string }) {
  const prepared = readPreparedExport(value, campaignId);
  const [busyAction, setBusyAction] = useState<'download' | 'filters' | null>(null);
  const busy = busyAction !== null;
  const [message, setMessage] = useState('');
  const [editing, setEditing] = useState<InterviewSession | null>(null);
  const request = useRef<AbortController | null>(null);
  useEffect(() => () => request.current?.abort(), []);
  if (!prepared) return null;

  /** Load current access and schema before editing or downloading a saved action. */
  async function run(edit: boolean) {
    if (request.current || !prepared) return;
    const controller = new AbortController();
    request.current = controller;
    setBusyAction(edit ? 'filters' : 'download');
    setMessage('');
    try {
      const { getInterviewSession } = await import('../../services/interviewService');
      controller.signal.throwIfAborted();
      const session = await getInterviewSession(campaignId);
      controller.signal.throwIfAborted();
      if (!session || session.id !== campaignId) throw new Error('Campaign unavailable');
      if (edit) setEditing(session);
      else {
        validateExportFilters(prepared.filters, session.form_schema ?? []);
        const count = await downloadCampaignCsv(session, prepared.filters, controller.signal, prepared.conversationIds);
        if (!controller.signal.aborted) setMessage(count ? 'Download started.' : 'No responses match these filters.');
      }
    } catch (error) {
      if (!controller.signal.aborted) {
        captureException(error instanceof Error ? error : new Error('CSV export failed'), 'CampaignCsvExportCard.download', { sessionId: campaignId });
        setMessage('Could not export CSV.');
      }
    } finally {
      if (!controller.signal.aborted) setBusyAction(null);
      if (request.current === controller) request.current = null;
    }
  }

  return <>
    <div className="my-2 rounded-xl border border-border bg-surface p-4">
      <div className="flex items-start gap-3">
        <IconFileSpreadsheet aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-foreground">{prepared.campaignName}.csv</p>
          <p className="mt-1 break-words text-sm text-muted-foreground">{prepared.summary}</p>
        </div>
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button aria-busy={busyAction === 'download'} type="button" disabled={busy} onClick={() => void run(false)} className="flex items-center gap-2 rounded-lg bg-accent px-3 py-2 text-sm font-medium text-accent-foreground hover:bg-accent-hover disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-ring">
          <ButtonContent loading={busyAction === 'download'}><IconDownload aria-hidden="true" className="h-4 w-4" />Download CSV</ButtonContent>
        </button>
        <button aria-busy={busyAction === 'filters'} type="button" disabled={busy} onClick={() => void run(true)} className="rounded-lg px-2 py-2 text-sm text-muted-foreground hover:bg-surface-hover focus-visible:outline-2 focus-visible:outline-ring"><ButtonContent loading={busyAction === 'filters'}>Edit filters</ButtonContent></button>
      </div>
      {message && <p role="status" className="mt-3 text-sm text-muted-foreground">{message}</p>}
    </div>
    {editing && <CampaignExportModal session={editing} initialFilters={prepared.filters} conversationIds={prepared.conversationIds} onClose={() => setEditing(null)} />}
  </>;
}
